import { type FormEvent, useState } from "react";

import {
  type Batch,
  type Entry,
  type ExposurePolicy,
  type IntakeMethod,
  type Rights,
  type Summary,
  failBatch,
  getSummary,
  listEntries,
  openBatch,
  relativePath,
  sealBatch,
  uploadFile,
  uploadPackage,
} from "./intakeClient";

interface Progress {
  path: string;
  sent: number;
  total: number;
  status: "uploading" | "done" | "error";
  detail?: string;
}

const METHODS: { value: IntakeMethod; label: string }[] = [
  { value: "upload", label: "Files" },
  { value: "folder", label: "Folder" },
  { value: "zip_package", label: "ZIP package" },
  { value: "manifest_batch", label: "Manifest batch" },
];

const PROVIDER_EXPOSURE: { value: Rights["provider_exposure"]; label: string }[] = [
  { value: "none", label: "None: nothing from these sources reaches a model provider" },
  { value: "policy_permitted", label: "Providers the engagement policy permits" },
  { value: "listed_providers", label: "Only the providers listed below" },
];

/** Comma- or space-separated provider names, lower-cased and de-duplicated. */
export function parseProviders(raw: string): string[] {
  return [...new Set(raw.split(/[\s,]+/).map((p) => p.trim().toLowerCase()).filter(Boolean))];
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : String(caught);
}

/** Corpus intake (DESIGN-SPEC 13): open a batch for a Corpus Run, add files, folders, ZIP
 * packages, or a manifest-declared set, then seal the inventory or fail the batch visibly. */
export function SourcesIntakePage() {
  const [batch, setBatch] = useState<Batch | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [progress, setProgress] = useState<Progress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [providerExposure, setProviderExposure] = useState<Rights["provider_exposure"]>("none");
  const noExposure = providerExposure === "none";

  async function refresh(batchId: string) {
    const [s, e] = await Promise.all([getSummary(batchId), listEntries(batchId)]);
    setSummary(s);
    setBatch(s.batch);
    setEntries(e);
  }

  async function onOpen(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const form = new FormData(event.currentTarget);
    const method = form.get("method") as IntakeMethod;
    const providers =
      providerExposure === "listed_providers" ? parseProviders(String(form.get("providers") ?? "")) : [];
    if (providerExposure === "listed_providers" && providers.length === 0) {
      setError("list at least one provider, or choose another provider exposure");
      return;
    }
    const rights: Rights = {
      access_classification: form.get("classification") as Rights["access_classification"],
      permitted_processing_locations: [String(form.get("location") || "customer-site")],
      provider_exposure: providerExposure,
      permitted_providers: providers,
      verbatim_output_allowed: form.get("verbatim") === "on",
      rag_corpus_allowed: form.get("rag") === "on",
    };
    const sw = (name: string): "allow" | "deny" => (form.get(name) === "on" ? "allow" : "deny");
    const owner = String(form.get("owner") ?? "").trim();
    const redaction = String(form.get("redaction_profile") ?? "").trim();
    // With no provider exposure the switches are disabled (and not submitted): record denials.
    const exposure: ExposurePolicy = {
      text_exposure: noExposure ? "deny" : sw("expose_text"),
      image_exposure: noExposure ? "deny" : sw("expose_images"),
      document_exposure: noExposure ? "deny" : sw("expose_documents"),
      ...(owner ? { owner } : {}),
      ...(redaction && !noExposure ? { redaction_profile: redaction } : {}),
    };
    const manifestFile = form.get("manifest");
    try {
      let manifest: { format: "csv" | "json"; content: string } | undefined;
      if (method === "manifest_batch") {
        if (!(manifestFile instanceof File) || manifestFile.size === 0) {
          throw new Error("choose the manifest file (CSV or JSON) for a manifest batch");
        }
        manifest = {
          format: manifestFile.name.toLowerCase().endsWith(".json") ? "json" : "csv",
          content: await manifestFile.text(),
        };
      }
      const runId = String(form.get("run_id") ?? "").trim();
      const opened = await openBatch({
        engagement_id: String(form.get("engagement_id")).trim(),
        batch_key: String(form.get("batch_key")).trim(),
        intake_method: method,
        default_rights: rights,
        default_exposure: exposure,
        reason: String(form.get("reason")).trim(),
        ...(runId ? { run_id: runId } : {}),
        ...(manifest ? { manifest } : {}),
      });
      setBatch(opened);
      await refresh(opened.id);
    } catch (caught) {
      setError(message(caught));
    }
  }

  async function addFiles(files: FileList | null, asPackages: boolean) {
    if (!batch || !files) return;
    setBusy(true);
    setError(null);
    for (const file of Array.from(files)) {
      const path = relativePath(file);
      setProgress((p) => [...p, { path, sent: 0, total: file.size, status: "uploading" }]);
      const update = (change: Partial<Progress>) =>
        setProgress((p) => p.map((item) => (item.path === path ? { ...item, ...change } : item)));
      try {
        if (asPackages) {
          await uploadPackage(batch.id, path, file);
        } else {
          await uploadFile(batch.id, path, file, (sent, total) => update({ sent, total }));
        }
        update({ status: "done", sent: file.size });
      } catch (caught) {
        update({ status: "error", detail: message(caught) });
      }
    }
    await refresh(batch.id).catch((caught: unknown) => setError(message(caught)));
    setBusy(false);
  }

  async function onSeal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!batch) return;
    const form = new FormData(event.currentTarget);
    try {
      await sealBatch(batch.id, String(form.get("seal_reason")), form.get("accept_missing") === "on");
      await refresh(batch.id);
    } catch (caught) {
      setError(message(caught));
    }
  }

  async function onFail(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!batch) return;
    const form = new FormData(event.currentTarget);
    try {
      await failBatch(batch.id, String(form.get("fail_reason")));
      await refresh(batch.id);
    } catch (caught) {
      setError(message(caught));
    }
  }

  return (
    <section aria-labelledby="intake-title">
      <h1 id="intake-title">Source intake</h1>
      {error && <p role="alert">{error}</p>}

      {!batch && (
        <form onSubmit={onOpen} aria-label="Open intake batch">
          <label>
            Engagement id <input name="engagement_id" required />
          </label>
          <label>
            Batch key <input name="batch_key" required pattern="[a-z0-9][a-z0-9._\-]{1,62}" />
          </label>
          <label>
            Intake method{" "}
            <select name="method" defaultValue="upload">
              {METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Corpus Run id (optional) <input name="run_id" />
          </label>
          <label>
            Manifest file (manifest batch) <input name="manifest" type="file" accept=".csv,.json" />
          </label>
          <fieldset>
            <legend>Default rights for these sources</legend>
            <label>
              Access classification{" "}
              <select name="classification" defaultValue="internal">
                <option value="public">Public</option>
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
            </label>
            <label>
              Processing location <input name="location" defaultValue="customer-site" />
            </label>
            <label>
              <input name="rag" type="checkbox" defaultChecked /> May enter the governed RAG corpus
            </label>
            <label>
              <input name="verbatim" type="checkbox" defaultChecked /> Verbatim content may appear in
              outputs
            </label>
          </fieldset>
          <fieldset>
            <legend>What may reach a model provider (per source; the engagement policy applies too)</legend>
            <label>
              Owner <input name="owner" />
            </label>
            <label>
              Provider exposure{" "}
              <select
                name="provider_exposure"
                value={providerExposure}
                onChange={(e) => setProviderExposure(e.currentTarget.value as Rights["provider_exposure"])}
              >
                {PROVIDER_EXPOSURE.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {providerExposure === "listed_providers" && (
              <label>
                Providers (comma separated) <input name="providers" required />
              </label>
            )}
            {noExposure && (
              <p role="note">
                No content from these sources is sent to any provider, so the switches below do not
                apply. Choose a provider exposure to enable them.
              </p>
            )}
            <label>
              <input name="expose_text" type="checkbox" defaultChecked disabled={noExposure} /> Text
              passages
            </label>
            <label>
              <input name="expose_images" type="checkbox" disabled={noExposure} /> Images
            </label>
            <label>
              <input name="expose_documents" type="checkbox" disabled={noExposure} /> Whole documents
            </label>
            <label>
              Redaction profile (an engagement policy redaction rule){" "}
              <input name="redaction_profile" pattern="[a-z][a-z0-9_]*" disabled={noExposure} />
            </label>
          </fieldset>
          <label>
            Reason <input name="reason" required />
          </label>
          <button type="submit">Open batch</button>
        </form>
      )}

      {batch && (
        <>
          <h2>
            Batch {batch.batch_key} <span data-testid="batch-state">({batch.state})</span>
          </h2>
          {batch.run_id && <p>Corpus Run {batch.run_id}</p>}
          <p>
            <a href={`/sources/inventory?batch=${batch.id}`}>Inventory of this batch</a>
            {batch.run_id && (
              <>
                {" · "}
                <a href={`/sources/inventory?engagement=${batch.engagement_id}&run=${batch.run_id}`}>
                  Inventory of the Corpus Run
                </a>
              </>
            )}
          </p>
          {batch.failure_reason && <p role="status">Failed: {batch.failure_reason}</p>}

          {batch.state === "open" && (
            <div>
              <label>
                Add files{" "}
                <input
                  type="file"
                  multiple
                  disabled={busy}
                  onChange={(e) => void addFiles(e.currentTarget.files, false)}
                />
              </label>
              <label>
                Add a folder{" "}
                <input
                  type="file"
                  multiple
                  disabled={busy}
                  {...{ webkitdirectory: "", directory: "" }}
                  onChange={(e) => void addFiles(e.currentTarget.files, false)}
                />
              </label>
              <label>
                Add ZIP packages{" "}
                <input
                  type="file"
                  multiple
                  accept=".zip"
                  disabled={busy}
                  onChange={(e) => void addFiles(e.currentTarget.files, true)}
                />
              </label>
            </div>
          )}

          {progress.length > 0 && (
            <ul aria-label="Uploads">
              {progress.map((p) => (
                <li key={p.path}>
                  {p.path}: {p.status === "uploading" ? `${p.sent} / ${p.total} bytes` : p.status}
                  {p.detail ? ` (${p.detail})` : ""}
                </li>
              ))}
            </ul>
          )}

          {summary && (
            <p data-testid="batch-summary">
              {Object.entries(summary.entries_by_status)
                .map(([status, n]) => `${n} ${status}`)
                .join(", ") || "No files yet"}
              {summary.missing_declared.length > 0 &&
                ` · ${summary.missing_declared.length} declared file(s) not received`}
              {summary.inventory_sha256 && ` · inventory ${summary.inventory_sha256.slice(0, 12)}`}
            </p>
          )}
          {summary?.declared_lineage && summary.declared_lineage.length > 0 && (
            <ul aria-label="Declared lineage">
              {summary.declared_lineage.map((link) => (
                <li key={`${link.from_path}->${link.to_path}:${link.relationship}`}>
                  {link.from_path} {link.declared_as.replace(/_/g, " ")} {link.to_path}: {link.status}
                  {link.detail ? ` (${link.detail})` : ""}
                </li>
              ))}
            </ul>
          )}

          <table>
            <thead>
              <tr>
                <th>Path</th>
                <th>Status</th>
                <th>SHA-256</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.entry_id}>
                  <td>{e.path}</td>
                  <td>{e.status}</td>
                  <td>{e.sha256 ? e.sha256.slice(0, 12) : ""}</td>
                  <td>{e.reason ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {batch.state === "open" && (
            <>
              <form onSubmit={onSeal} aria-label="Seal batch">
                <label>
                  Reason <input name="seal_reason" required />
                </label>
                {summary && summary.missing_declared.length > 0 && (
                  <label>
                    <input name="accept_missing" type="checkbox" /> Record missing declared files as
                    rejected
                  </label>
                )}
                <button type="submit" disabled={busy}>
                  Seal inventory
                </button>
              </form>
              <form onSubmit={onFail} aria-label="Fail batch">
                <label>
                  Reason <input name="fail_reason" required />
                </label>
                <button type="submit" disabled={busy}>
                  Fail batch
                </button>
              </form>
            </>
          )}
        </>
      )}
    </section>
  );
}
